import { useSyncExternalStore } from 'react';
import { BaseBlock } from '@/declarations/models/blocks/BaseBlock';

/** A module kept for pasting on other pages */
export interface ModuleClipboardItem {
  key: string;
  block: BaseBlock;
  siteId?: number;
  pageId?: number;
  pageTitle?: string;
  addedAt: string;
}

const STORAGE_KEY = 'm24.pageEditor.moduleClipboard';
const MAX_ITEMS = 30;
const EMPTY: Array<ModuleClipboardItem> = [];
const listeners = new Set<() => void>();

const read = (): Array<ModuleClipboardItem> => {
  try {
    const items = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
    return Array.isArray(items) ? items : EMPTY;
  } catch {
    return EMPTY;
  }
};

let current = read();

const write = (items: Array<ModuleClipboardItem>) => {
  current = items;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  } catch {
    // Kept for this session only, e.g. when the storage is full
  }
  listeners.forEach((listener) => listener());
};

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  // Other tabs
  const onStorage = (e: StorageEvent) => {
    if (e.key !== STORAGE_KEY) return;
    current = read();
    listener();
  };
  window.addEventListener('storage', onStorage);
  return () => {
    listeners.delete(listener);
    window.removeEventListener('storage', onStorage);
  };
};

export const moduleClipboard = {
  add: (item: Omit<ModuleClipboardItem, 'key' | 'addedAt'>) =>
    write(
      [
        { ...item, key: `${Date.now()}-${Math.random().toString(36).slice(2)}`, addedAt: new Date().toISOString() },
        ...current,
      ].slice(0, MAX_ITEMS),
    ),
  remove: (key: string) => write(current.filter((i) => i.key !== key)),
  clear: () => write([]),
};

/** Modules kept across pages and tabs in this browser, newest first */
export const useModuleClipboard = (): Array<ModuleClipboardItem> => useSyncExternalStore(subscribe, () => current);
